<div class="chat-sidebar" [class.chat-sidebar--compact]="compact">
  <!-- Search + own status -->
  <div class="chat-sidebar__top">
    <div class="chat-sidebar__search">
      <i class="pi pi-search"></i>
      <input type="text" placeholder="Search people or groups..." [(ngModel)]="searchTerm" aria-label="Search people or groups" />
      <button type="button" class="chat-sidebar__clear" *ngIf="searchTerm" (click)="searchTerm = ''" aria-label="Clear search">
        <i class="pi pi-times"></i>
      </button>
    </div>

    <div class="chat-sidebar__status" *ngIf="!compact">
      <app-chat-status-menu></app-chat-status-menu>
    </div>

    <button type="button" class="chat-sidebar__new-group" *ngIf="!compact && !store.chatUnavailable()" (click)="createGroup.emit()">
      <i class="pi pi-plus"></i>
      New Group
    </button>

    <!-- Master Admin only: Quick Message opens pre-ticked with everyone online, but the picker
         inside also reaches offline colleagues and offers Select All. -->
    <button
      type="button"
      class="chat-sidebar__message-online"
      *ngIf="!compact && store.amMasterAdmin && store.onlinePeople().length"
      (click)="store.beginQuickMessage()"
      [title]="'Quick-message colleagues — starts with all ' + store.onlinePeople().length + ' online now'">
      <i class="pi pi-send"></i>
      Message Online ({{ store.onlinePeople().length }})
    </button>

    <!-- Quick filters. They wrap rather than scroll, so none is ever clipped at the edge. -->
    <div class="chat-filters" role="tablist" aria-label="Filter conversations" *ngIf="!store.chatUnavailable()">
      <button
        type="button"
        class="chat-filters__chip"
        *ngFor="let option of store.filterOptions(); trackBy: trackByFilter"
        role="tab"
        [class.is-active]="store.activeFilter() === option.key"
        [attr.aria-selected]="store.activeFilter() === option.key"
        (click)="store.setFilter(option.key)">
        <i class="pi pi-star-fill" *ngIf="option.key === 'favourites'"></i>
        <i class="pi pi-video" *ngIf="option.key === 'calls'"></i>
        <i class="pi pi-eye-slash" *ngIf="option.key === 'hidden'"></i>
        {{ option.label }}
        <span class="chat-filters__count" *ngIf="option.count">{{ option.count > 99 ? '99+' : option.count }}</span>
      </button>
    </div>
  </div>

  <div class="chat-sidebar__scroll">
    <!--
      No chat API answering here. Deliberately not behind `!compact` — the "Demo data" chip this
      replaced was, which meant the floating widget showed fabricated conversations with no hint at
      all that they were fabricated. Nothing else in the list renders while this is up: an empty
      roster with no explanation reads as "nobody is here", which is a different and wrong message.
    -->
    <div class="chat-sidebar__offline" *ngIf="store.chatUnavailable()">
      <i class="pi pi-exclamation-triangle"></i>
      <p class="chat-sidebar__offline-title">Please refresh your browser</p>
      <span class="chat-sidebar__offline-hint">Chat could not reach the server.</span>
    </div>

    <ng-container *ngIf="!store.chatUnavailable()">
      <!-- A star that did not reach the server has to say so — it will not be there tomorrow -->
      <p class="chat-sidebar__warning" *ngIf="store.favouritesError()">
        <i class="pi pi-exclamation-triangle"></i>
        {{ store.favouritesError() }}
      </p>

      <!-- Nothing to show: names the filter rather than saying "no results" for a list you filtered -->
      <p class="chat-sidebar__empty" *ngIf="store.emptyStateText() && !store.loadingSidebar()">{{ store.emptyStateText() }}</p>
    </ng-container>

    <!--
      One list: groups and people interleaved, ordered by unread → favourites → recency → the rest
      of the roster. Sections and their collapse chevrons are gone — whoever just messaged you was
      in one of two lists and possibly behind a collapsed header, which is the opposite of what a
      sidebar is for.
    -->
    <ul class="chat-sidebar__list" *ngIf="!store.chatUnavailable()">
      <li
        *ngFor="let row of store.filteredRows(); trackBy: trackByRow"
        class="chat-row"
        [class.is-active]="isActive(conversationIdOf(row))"
        [class.is-unread]="unreadOf(row) > 0"
        [class.is-inactive]="isInactive(row)"
        [class.is-hidden]="isHidden(row)"
        [attr.title]="isInactive(row) ? row.person!.name + ' has been deactivated' : null"
        (click)="openRow(row)"
        (contextmenu)="onRowContextMenu($event, row)">
        <app-chat-avatar
          *ngIf="row.kind === 'person'"
          [name]="row.person!.name"
          [avatar]="row.person!.avatar_url"
          [status]="row.person!.status"
          [size]="compact ? 32 : 36"></app-chat-avatar>

        <span class="chat-row__group-icon" *ngIf="row.kind === 'group'" [style.width.px]="compact ? 32 : 36" [style.height.px]="compact ? 32 : 36">
          <i class="pi pi-users"></i>
        </span>

        <div class="chat-row__body">
          <div class="chat-row__line">
            <span class="chat-row__name">{{ nameOf(row) }}</span>
            <span class="chat-row__time" *ngIf="lastMessageOf(row)">{{ shortAgo(lastMessageOf(row)!.created_at) }}</span>

            <!-- Master Admin only, and never on your own row. A direct click beats the right-click
                 menu for discoverability — not every trackpad/browser combination sends a
                 `contextmenu` event reliably, and this needs no explanation to find. -->
            <button
              type="button"
              class="chat-row__hide"
              *ngIf="row.kind === 'person' && store.amMasterAdmin && row.person.user_id !== store.myId"
              [class.is-on]="isHidden(row)"
              (click)="toggleHidden($event, row)"
              [attr.title]="isHidden(row) ? 'Unhide from chat' : 'Hide from chat'"
              [attr.aria-label]="(isHidden(row) ? 'Unhide ' : 'Hide ') + nameOf(row) + ' from chat'"
              [attr.aria-pressed]="isHidden(row)">
              <i class="pi" [ngClass]="isHidden(row) ? 'pi-eye' : 'pi-eye-slash'"></i>
            </button>

            <!-- Sits in the top-right corner, next to the timestamp — collapses to zero width
                 rather than just fading out, so 78 idle rows don't reserve a gap nothing fills. -->
            <button
              type="button"
              class="chat-row__star"
              [class.is-on]="isFavourite(row)"
              (click)="toggleFavourite($event, row)"
              [attr.title]="isFavourite(row) ? 'Remove from favourites' : 'Add to favourites'"
              [attr.aria-label]="(isFavourite(row) ? 'Remove ' : 'Add ') + nameOf(row) + ' to favourites'"
              [attr.aria-pressed]="isFavourite(row)">
              <i class="pi" [ngClass]="isFavourite(row) ? 'pi-star-fill' : 'pi-star'"></i>
            </button>
          </div>
          <div class="chat-row__line">
            <!-- A live "typing…" outranks the subtitle: the subtitle is history, this isn't -->
            <span class="chat-row__typing" *ngIf="typingOf(row) as typing; else subtitleTpl">
              <span class="chat-row__dots"><i></i><i></i><i></i></span>
              {{ typing }}
            </span>
            <!-- Falls back to what the row is when there is no history (see subtitleOf). A person
                 with nothing to say has no second line — the *ngIf keeps the
                 span out of the DOM rather than leaving an empty one holding the row open. -->
            <ng-template #subtitleTpl>
              <span class="chat-row__preview" *ngIf="subtitleOf(row) as subtitle" [class.chat-row__preview--muted]="!lastMessageOf(row)">{{
                subtitle
              }}</span>
            </ng-template>
            <span class="chat-row__badge" *ngIf="unreadOf(row)">{{ unreadOf(row) > 99 ? '99+' : unreadOf(row) }}</span>
          </div>
        </div>
      </li>
    </ul>

    <div class="chat-sidebar__loading" *ngIf="store.loadingSidebar() && !store.filteredRows().length && !store.chatUnavailable()">
      <i class="pi pi-spin pi-spinner"></i>
      Loading your office…
    </div>
  </div>
</div>

<app-chat-context-menu
  *ngIf="contextMenuPerson"
  [x]="contextMenuX"
  [y]="contextMenuY"
  [items]="contextMenuItems"
  (picked)="onContextMenuPicked($event)"
  (closed)="closeContextMenu()"></app-chat-context-menu>
